import { TFunction } from "i18next";
import * as React from "react";

import { LimitsAndClosureDataType } from "../../containers/LimitsAndClosureContainter/LimitsAndClosureContainer";
import { LimitsAndClosureContext } from "../../context/LimitsAndClosureContext";
import { InputButton, SelectButton } from "../AccountButton/AccountButton";
import GenericButton from "../GenericButton/GenericButton";
import confirmationPopupText, {
    provideEndDateOfBlock
} from "../LimitDialogText/LimitDialogText";
import LimitDisclaimer from "../LimitDisclaimer/LimitDisclaimer";
import { LimitAndClosureScreens } from "../LimitsAndClosure/LimitsAndClosure";
import LimitsAndClosureText from "../LimitsAndClosureText/LimitsAndClosureText";

import {
    AnimatedDisclaimerDialog,
    AnimatedLimitDialog,
    AnimatedLimitNumpadDialog
} from "../LimitsDialog/AnimatedLimitDialog";

// tslint:disable-next-line:no-var-requires
const styles = require("./LimitComponent.scss");

export interface LimitProps {
    navigationHandler: (e: React.BaseSyntheticEvent) => void;
    screen: string;
    t: TFunction;
}
/* tslint:disable:jsx-no-multiline-js */
const LimitComponent = ({ navigationHandler, screen, t }: LimitProps) => {
    const {
        editable,
        handleCancelClick,
        handleEditClick,
        handleSaveClick,
        getData,
        getSavedData,
        setAmount,
        setReason,
        setValidity,
        getRawData
    } = React.useContext(LimitsAndClosureContext);

    const {
        country,
        data: { amount, validity, selectedValidityKey, reason }
    } = getData(screen);

    const futureLimit = getData(screen).futureLimit;
    const rgmPayinLimit = getRawData("PAYIN_RGM");

    const {
        dialog,
        renderDisclaimerDialog,
        renderLimitDialog,
        renderReasonDialog,
        renderValidityDialog,
        renderExclusionDialog
    } = dialogHandler({
        getData,
        getSavedData,
        handleSaveClick,
        rgmPayinLimit,
        screen,
        setAmount,
        setReason,
        setValidity,
        t
    });
    const exclusion = screen === LimitAndClosureScreens.exclusion;
    const countryLC = country.toLowerCase();
    const exclusionAt = exclusion && countryLC === "at";
    const textKey = exclusionAt ? "Text-AT" : "Text";
    const validityKey = exclusion ? selectedValidityKey || -1 : validity;
    const validityField = exclusionAt ? "Validity-AT" : "Validity";

    return (
        <>
            {renderLimitDisclaimer(t, screen, futureLimit)}
            <LimitsAndClosureText
                header={t(`${screen}.Header`)}
                text={t(`${screen}.${textKey}`)}
                handleClick={navigationHandler}
                screen={screen}
                buttonText={t(`${screen}.ButtonText`)}
            />
            <div className={styles.wrapper}>
                {screen !== LimitAndClosureScreens.accountClosure &&
                    screen !== LimitAndClosureScreens.exclusion && (
                        <InputButton
                            onClick={renderLimitDialog}
                            value={amount}
                            disabled={!editable}
                            placeholder={t("AmountInEurPlaceholder")}
                            label={t("AmountInEurLabel")}
                        />
                    )}
                {screen === LimitAndClosureScreens.accountClosure && (
                    <SelectButton
                        onClick={renderReasonDialog}
                        value={getValueByIdx(reason, t, screen, "Reason")}
                        label={t(`${screen}.ReasonLabel`)}
                        disabled={!editable}
                        placeholder={t(`${screen}.ReasonPlaceholder`)}
                    />
                )}
                <SelectButton
                    onClick={
                        exclusion ? renderExclusionDialog : renderValidityDialog
                    }
                    // validity is for the legacy code support
                    value={getValueByIdx(
                        validityKey,
                        t,
                        screen,
                        validityField,
                        "Validity"
                    )}
                    label={t(`${screen}.ValidityLabel`)}
                    disabled={!editable}
                    placeholder={t(`${screen}.ValidityPlaceholder`)}
                />
            </div>
            <>{dialog}</>
            <div className={styles.controls}>
                {editable ? (
                    <>
                        <GenericButton
                            className={styles.editCancelBtn}
                            onClick={handleCancelClick}
                        >
                            {t("CancelBtn")}
                        </GenericButton>
                        <GenericButton
                            onClick={renderDisclaimerDialog}
                            className={styles.saveBtn}
                        >
                            {t("SaveBtn")}
                        </GenericButton>
                    </>
                ) : (
                    <GenericButton
                        className={styles.editCancelBtn}
                        onClick={handleEditClick}
                    >
                        {t("EditBtn")}
                    </GenericButton>
                )}
            </div>
        </>
    );
};

type DialogHandlerProps = {
    t: any;
    screen: any;
    setValidity: any;
    setAmount: any;
    setReason: any;
    getData: any;
    getSavedData: any;
    handleSaveClick: any;
    rgmPayinLimit: any;
};

const validityDays = (validity: number): number => {
    return validity === 1 ? 7 : validity === 2 ? 30 : 1;
};

const dialogHandler = ({
    t,
    screen,
    setValidity,
    setAmount,
    setReason,
    getData,
    getSavedData,
    handleSaveClick,
    rgmPayinLimit
}: DialogHandlerProps) => {
    const [dialog, setDialog] = React.useState<React.ReactNode>(null);
    const renderFunc = () => true;

    const newStateOverall = getData(screen);
    const newStateData = newStateOverall.data;
    const prevStateData = getSavedData(screen).data;
    const newStateAmount =
        newStateData.amount / validityDays(newStateData.validity);
    const prevStateAmount =
        newStateData.amount / validityDays(prevStateData.validity);
    const validityLabel =
        newStateOverall.country.toLowerCase() === "at"
            ? "ValidityLabel-AT"
            : "ValidityLabel";

    // tslint:disable:jsx-no-lambda
    const renderDisclaimerDialog = () => {
        const newState = newStateAmount;
        const newValidity = newStateData.validity;
        const previousState = prevStateAmount;
        const dateOfEnd =
            newStateData.selectedValidityKey || newStateData.validity;
        const rgmLimitReached = isRgmLimitReached(
            rgmPayinLimit,
            newState,
            newValidity
        );
        setDialog(
            <AnimatedDisclaimerDialog
                handleInputClick={() => {
                    return;
                }}
                handleSaveClick={handleSaveClick}
                buttonCaptions={[t(`SaveBtn`), t(`CancelBtn`), t(`EddBtn`)]}
                isDesiredPayinLimitTooHigh={rgmLimitReached}
                text={
                    rgmLimitReached
                        ? t(`${screen}.RgmLimitReachedText`, {
                              rgmLimitValidity: t(`${screen}.Validity`, {
                                  returnObjects: true
                              })[rgmPayinLimit.data.validity],
                              rgmLimitValue: rgmPayinLimit.data.amount
                          })
                        : t(
                              confirmationPopupText({
                                  dateOfEnd,
                                  newState,
                                  previousState,
                                  screen
                              }),
                              provideEndDateOfBlock(screen, dateOfEnd)
                          )
                }
                renderDialogHook={renderFunc}
                title={
                    rgmLimitReached
                        ? t(`RgmLimitReachedHeader`)
                        : t(`DisclaimerDialogHeader`)
                }
                buttons={[]}
            />
        );
    };

    const isRgmLimitReached = (
        // tslint:disable-next-line:no-shadowed-variable
        rgmPayinLimit: LimitsAndClosureDataType,
        newLimit: number,
        newValidity: number
    ) => {
        const reached =
            LimitAndClosureScreens.payInLimit === screen &&
            rgmPayinLimit != null &&
            rgmPayinLimit.data.amount > 0 &&
            Validity.MONTH === rgmPayinLimit.data.validity &&
            ((Validity.DAY === Number(newValidity) &&
                newLimit * 30 > rgmPayinLimit.data.amount) ||
                (Validity.WEEK === Number(newValidity) &&
                    newLimit * 4 > rgmPayinLimit.data.amount) ||
                (Validity.MONTH === Number(newValidity) &&
                    newLimit > rgmPayinLimit.data.amount));
        // tslint:disable-next-line:no-console
        console.log(
            `checked rgm limit reached: newValidity=${newValidity} newLimit=${newLimit} isReached=${reached}`
        );
        return reached;
    };

    const Validity = {
        DAY: 0,
        MONTH: 2,
        WEEK: 1
    } as const;

    const renderLimitDialog = () => {
        setDialog(
            <AnimatedLimitNumpadDialog
                renderDialogHook={renderFunc}
                handleInputClick={setAmount}
                title={t("AmountInEurLabel")}
                buttons={[]}
            />
        );
    };

    const renderExclusionDialog = () => {
        const countryLC = newStateOverall.country.toLowerCase();
        const countryAt = countryLC === "at";
        const showNew =
            newStateOverall.showNewShortTermExclusions && !countryAt;
        const validityKey = countryAt ? "Validity-AT" : "Validity";
        const defaultValidityKeys = countryAt
            ? ["P183D", "P365D", "INDEFINITE"]
            : ["P180D", "P366D", "INDEFINITE"];
        const validityKeys = showNew
            ? newStateData.displayableValidityKeys
            : defaultValidityKeys;
        const buttons = validityKeys.map((vk: string, idx: number) => ({
            index: idx,
            key: vk,
            translation: t(`${screen}.${validityKey}.${vk}`)
        }));
        setDialog(
            <AnimatedLimitDialog
                renderDialogHook={renderFunc}
                handleInputClick={setValidity}
                title={t(`${screen}.${validityLabel}`)}
                buttons={buttons}
                exclusion={true}
            />
        );
    };

    const renderValidityDialog = () => {
        setDialog(
            <AnimatedLimitDialog
                renderDialogHook={renderFunc}
                handleInputClick={setValidity}
                title={t(`${screen}.${validityLabel}`)}
                buttons={t(`${screen}.Validity`, {
                    returnObjects: true
                })}
            />
        );
    };

    const renderReasonDialog = () => {
        setDialog(
            <AnimatedLimitDialog
                renderDialogHook={renderFunc}
                handleInputClick={setReason}
                title={t(`${screen}.ReasonLabel`)}
                buttons={t(`${screen}.Reason`, {
                    returnObjects: true
                })}
            />
        );
    };

    return {
        dialog,
        renderDisclaimerDialog,
        renderExclusionDialog,
        renderLimitDialog,
        renderReasonDialog,
        renderValidityDialog
    };
};

const renderLimitDisclaimer = (
    t: TFunction,
    screen: string,
    futureLimit: LimitsAndClosureDataType["futureLimit"]
) => {
    return (
        <>
            {screen !== LimitAndClosureScreens.accountClosure &&
            screen !== LimitAndClosureScreens.exclusion ? (
                <LimitDisclaimer
                    t={t}
                    screen={screen}
                    futureLimit={futureLimit}
                />
            ) : (
                <div />
            )}
        </>
    );
};

const getValueByIdx = (
    index: number | string,
    t: TFunction,
    screen: string,
    field: string,
    fieldPlaceholder?: string
) => {
    if (typeof index === "string") {
        return (t(`${screen}.${field}`, {
            returnObjects: true
        }) as { [k: string]: string })[index];
    }

    return index < 0
        ? t(
              `${screen}.${
                  fieldPlaceholder ? fieldPlaceholder : field
              }Placeholder`
          )
        : t(`${screen}.${field}`, { returnObjects: true })[index];
};

export default LimitComponent;
